import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft, RefreshCw, Radio, MapPin, Calendar, Clock,
  TrendingUp, Package, Users, ShieldAlert, CircleDot,
  ChevronDown, Trophy, Gavel, Beef,
} from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import dashboardService from '../../services/dashboardService'

// ─── Utilitários ─────────────────────────────────────────────────────────────
const fmtCurrency = (v) => {
  if (!v && v !== 0) return 'R$ 0,00'
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const fmtDate = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric',
  })
}

const initials = (nome) => {
  if (!nome) return '?'
  const parts = nome.trim().split(' ').filter(Boolean)
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-orange-500', 'bg-teal-500',
]
const avatarColor = (id) => AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length]

const MEDAL = ['🥇', '🥈', '🥉']

// ─── LiveBadge ───────────────────────────────────────────────────────────────
function LiveBadge({ isLive }) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold
      ${isLive ? 'bg-red-500/20 text-red-400 border border-red-500/40' : 'bg-gray-700/40 text-gray-400 border border-gray-600/40'}`}>
      <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-red-400 animate-pulse' : 'bg-gray-500'}`} />
      {isLive ? 'AO VIVO' : 'ENCERRADO'}
    </div>
  )
}

// ─── ProgressBar ─────────────────────────────────────────────────────────────
function ProgressBar({ vendidos, defendidos, total }) {
  const pctVendidos = total ? (vendidos / total) * 100 : 0
  const pctDefendidos = total ? (defendidos / total) * 100 : 0
  const pctTotal = Math.min(pctVendidos + pctDefendidos, 100)
  const pendentes = total - vendidos - defendidos

  return (
    <div>
      {/* Percentual centralizado e grande */}
      <div className="text-center mb-3">
        <span className="text-4xl sm:text-5xl font-black text-white tabular-nums leading-none">
          {pctTotal.toFixed(1)}%
        </span>
        <p className="text-xs text-gray-500 mt-1">do leilão concluído</p>
      </div>

      {/* Barra */}
      <div className="relative h-6 bg-gray-700/60 rounded-full overflow-hidden mb-3">
        <div
          className="absolute left-0 top-0 h-full bg-gradient-to-r from-emerald-600 to-emerald-400 transition-all duration-700"
          style={{ width: `${pctVendidos}%` }}
        />
        <div
          className="absolute top-0 h-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all duration-700"
          style={{ left: `${pctVendidos}%`, width: `${pctDefendidos}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold text-white drop-shadow">
            {vendidos + defendidos} / {total} lotes
          </span>
        </div>
      </div>

      {/* Legenda centralizada */}
      <div className="flex items-center justify-center gap-4 text-xs">
        <span className="flex items-center gap-1.5 text-emerald-400">
          <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 flex-shrink-0" />
          Vendidos <strong>{vendidos}</strong>
        </span>
        <span className="flex items-center gap-1.5 text-amber-400">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-500 flex-shrink-0" />
          Defesas <strong>{defendidos}</strong>
        </span>
        <span className="flex items-center gap-1.5 text-gray-400">
          <span className="w-2.5 h-2.5 rounded-sm bg-gray-600 flex-shrink-0" />
          Pendentes <strong>{pendentes}</strong>
        </span>
      </div>
    </div>
  )
}

// ─── StatCard ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, pct, accent }) {
  const accents = {
    emerald: 'border-emerald-500/30 bg-emerald-500/5',
    amber:   'border-amber-500/30 bg-amber-500/5',
    violet:  'border-violet-500/30 bg-violet-500/5',
    blue:    'border-blue-500/30 bg-blue-500/5',
    rose:    'border-rose-500/30 bg-rose-500/5',
    cyan:    'border-cyan-500/30 bg-cyan-500/5',
  }
  const iconAccents = {
    emerald: 'text-emerald-400 bg-emerald-500/15',
    amber:   'text-amber-400 bg-amber-500/15',
    violet:  'text-violet-400 bg-violet-500/15',
    blue:    'text-blue-400 bg-blue-500/15',
    rose:    'text-rose-400 bg-rose-500/15',
    cyan:    'text-cyan-400 bg-cyan-500/15',
  }
  const pctColors = {
    emerald: 'text-emerald-400',
    amber:   'text-amber-400',
    violet:  'text-violet-400',
    blue:    'text-blue-400',
    rose:    'text-rose-400',
    cyan:    'text-cyan-400',
  }

  return (
    <div className={`rounded-2xl border p-3 sm:p-5 flex flex-col gap-2 sm:gap-3 ${accents[accent] || accents.blue}`}>
      <div className="flex items-start justify-between">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${iconAccents[accent] || iconAccents.blue}`}>
          <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
        </div>
        {pct !== undefined && (
          <span className={`text-base sm:text-lg font-black tabular-nums ${pctColors[accent] || 'text-white'}`}>
            {pct}%
          </span>
        )}
      </div>
      <div>
        <p className="text-[10px] sm:text-xs text-gray-400 font-medium uppercase tracking-wider leading-tight">{label}</p>
        <p className="text-xl sm:text-2xl font-black text-white mt-0.5 tabular-nums leading-tight">{value}</p>
        {sub && <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  )
}

// ─── RankingCard ─────────────────────────────────────────────────────────────
function RankingCard({ title, icon: Icon, items, accent }) {
  const accents = {
    blue: { border: 'border-blue-500/20', header: 'text-blue-400', icon: 'bg-blue-500/15 text-blue-400' },
    emerald: { border: 'border-emerald-500/20', header: 'text-emerald-400', icon: 'bg-emerald-500/15 text-emerald-400' },
  }
  const a = accents[accent] || accents.blue

  if (!items?.length) {
    return (
      <div className={`rounded-2xl border bg-gray-800/40 p-6 ${a.border}`}>
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.icon}`}>
            <Icon className="w-4 h-4" />
          </div>
          <h3 className={`font-bold text-sm ${a.header}`}>{title}</h3>
        </div>
        <p className="text-gray-500 text-sm text-center py-6">Nenhum dado ainda</p>
      </div>
    )
  }

  const max = items[0]?.valor_total || 1

  return (
    <div className={`rounded-2xl border bg-gray-800/40 p-6 ${a.border}`}>
      <div className="flex items-center gap-2 mb-5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.icon}`}>
          <Icon className="w-4 h-4" />
        </div>
        <h3 className={`font-bold text-sm ${a.header}`}>{title}</h3>
        <span className="ml-auto text-xs text-gray-500">{items.length} participantes</span>
      </div>

      <div className="space-y-3">
        {items.map((item, i) => {
          const barPct = Math.round((item.valor_total / max) * 100)
          return (
            <div key={item.id} className="relative">
              {/* Barra de fundo */}
              <div
                className={`absolute inset-0 rounded-xl opacity-20 transition-all duration-500
                  ${i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-700' : 'bg-gray-700'}`}
                style={{ width: `${barPct}%` }}
              />

              <div className="relative flex items-center gap-3 p-3 rounded-xl">
                {/* Posição / Medal */}
                <div className="flex-shrink-0 w-7 text-center">
                  {i < 3 ? (
                    <span className="text-lg leading-none">{MEDAL[i]}</span>
                  ) : (
                    <span className="text-sm font-bold text-gray-500">{i + 1}°</span>
                  )}
                </div>

                {/* Avatar */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  text-xs font-bold text-white ${avatarColor(item.id)}`}>
                  {initials(item.nome)}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate leading-tight">
                    {item.nome}
                  </p>
                  <p className="text-xs text-gray-400">
                    {item.lotes} {item.lotes === 1 ? 'lote' : 'lotes'} · {item.animais} animais
                  </p>
                </div>

                {/* Valor */}
                <div className="flex-shrink-0 text-right">
                  <p className={`text-sm font-black tabular-nums
                    ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-600' : 'text-gray-400'}`}>
                    {fmtCurrency(item.valor_total)}
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Seletor de Leilão ────────────────────────────────────────────────────────
function LeilaoSelector({ leiloes, leilaoAtualId, onSelect, fullWidth = false }) {
  const [open, setOpen] = useState(false)
  const atual = leiloes?.find((l) => l.id === leilaoAtualId)

  if (!leiloes?.length) return null

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-2 bg-gray-800/80 border border-gray-700/60
          rounded-xl text-sm text-gray-200 hover:border-indigo-500/50 hover:bg-gray-700/60
          transition-all duration-150 ${fullWidth ? 'w-full' : ''}`}
      >
        <Gavel className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
        <span className="flex-1 truncate text-left font-medium">
          {atual?.nome || 'Selecionar leilão'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`absolute top-full mt-1 z-50 bg-gray-800 border border-gray-700/60
          rounded-xl shadow-2xl shadow-black/60 overflow-hidden
          ${fullWidth ? 'left-0 right-0' : 'right-0 w-80'}`}>
          {leiloes.map((l) => (
            <button
              key={l.id}
              onClick={() => { onSelect(l.id); setOpen(false) }}
              className={`w-full text-left px-4 py-3 flex flex-col gap-0.5 hover:bg-gray-700/60 transition-colors
                ${l.id === leilaoAtualId ? 'bg-indigo-500/10 border-l-2 border-indigo-500' : 'border-l-2 border-transparent'}`}
            >
              <span className="text-sm font-semibold text-white truncate">{l.nome}</span>
              <span className="text-xs text-gray-400">
                {fmtDate(l.data_leilao)} · {l.cidade}/{l.uf}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Countdown ───────────────────────────────────────────────────────────────
function RefreshCountdown({ seconds, total, onRefresh, loading }) {
  const pct = (seconds / total) * 100

  return (
    <button
      onClick={onRefresh}
      disabled={loading}
      className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/60 border border-gray-600/40
        rounded-xl text-xs text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors"
      title="Atualizar agora"
    >
      <div className="relative w-5 h-5">
        <svg className="w-5 h-5 -rotate-90" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="8" fill="none" stroke="#374151" strokeWidth="2" />
          <circle
            cx="10" cy="10" r="8" fill="none"
            stroke="#6366f1" strokeWidth="2"
            strokeDasharray={`${2 * Math.PI * 8}`}
            strokeDashoffset={`${2 * Math.PI * 8 * (1 - pct / 100)}`}
            className="transition-all duration-1000"
          />
        </svg>
        <RefreshCw className={`w-2.5 h-2.5 absolute inset-0 m-auto text-indigo-400 ${loading ? 'animate-spin' : ''}`} />
      </div>
      <span className="tabular-nums">{seconds}s</span>
    </button>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────
const REFRESH_INTERVAL = 15

export default function LeilaoTempoReal() {
  const navigate = useNavigate()
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [leilaoId, setLeilaoId] = useState(null)
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL)
  const [lastUpdate, setLastUpdate] = useState(null)

  const fetchData = useCallback(async (showLoader = false) => {
    if (!orgId) return
    if (showLoader) setLoading(true)
    setError(null)
    try {
      const result = await dashboardService.getLeilaoTempoReal(orgId, leilaoId)
      setData(result)
      setLastUpdate(new Date())
      setCountdown(REFRESH_INTERVAL)
    } catch (e) {
      setError('Não foi possível carregar os dados do leilão.')
    } finally {
      setLoading(false)
    }
  }, [orgId, leilaoId])

  // Carga inicial
  useEffect(() => {
    fetchData(true)
  }, [fetchData])

  // Countdown + auto-refresh
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          fetchData()
          return REFRESH_INTERVAL
        }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [fetchData])

  // Quando troca de leilão, recarrega
  const handleSelectLeilao = (id) => {
    setLeilaoId(id)
    setCountdown(REFRESH_INTERVAL)
  }

  const leilao = data?.leilao
  const stats = data?.stats
  const rankings = data?.rankings
  const leiloesRecentes = data?.leiloes_recentes || []

  const isLive = leilao?.status === 'on' && stats && stats.lotes_indefinidos > 0

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="border-b border-gray-800 bg-gray-900 sticky top-0 z-40">

        {/* Linha 1: voltar · título · status · countdown */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-12 sm:h-14 flex items-center gap-2">

          <button
            onClick={() => navigate('/dashboard')}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg
              hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          {/* Ícone + título — whitespace-nowrap evita quebra de linha */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="w-6 h-6 bg-indigo-500/20 rounded-md flex items-center justify-center">
              <Radio className="w-3.5 h-3.5 text-indigo-400" />
            </div>
            <span className="font-bold text-white text-sm whitespace-nowrap">
              Tempo Real
            </span>
          </div>

          {/* Espaço flexível */}
          <div className="flex-1" />

          {/* Status + contador */}
          <div className="flex items-center gap-1.5">
            {leilao && <LiveBadge isLive={isLive} />}
            <RefreshCountdown
              seconds={countdown}
              total={REFRESH_INTERVAL}
              onRefresh={() => fetchData(false)}
              loading={loading}
            />
          </div>

          {/* Seletor no desktop (inline) */}
          <div className="hidden sm:block">
            <LeilaoSelector
              leiloes={leiloesRecentes}
              leilaoAtualId={leilaoId || leilao?.id}
              onSelect={handleSelectLeilao}
            />
          </div>
        </div>

        {/* Linha 2 mobile: seletor largura total */}
        {leiloesRecentes.length > 0 && (
          <div className="sm:hidden px-3 pb-2">
            <LeilaoSelector
              leiloes={leiloesRecentes}
              leilaoAtualId={leilaoId || leilao?.id}
              onSelect={handleSelectLeilao}
              fullWidth
            />
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-8">

        {/* ── Estado vazio / erro ──────────────────────────────────────────── */}
        {error && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-rose-500/15 rounded-2xl flex items-center justify-center mb-4">
              <ShieldAlert className="w-8 h-8 text-rose-400" />
            </div>
            <p className="text-gray-300 font-medium">{error}</p>
            <button onClick={() => fetchData(true)} className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors">
              Tentar novamente
            </button>
          </div>
        )}

        {!error && !leilao && !loading && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mb-4">
              <Gavel className="w-8 h-8 text-gray-500" />
            </div>
            <p className="text-gray-400">Nenhum leilão encontrado para este organizador.</p>
          </div>
        )}

        {loading && !data && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin mb-3" />
            <p className="text-gray-400 text-sm">Carregando dados do leilão...</p>
          </div>
        )}

        {leilao && stats && (
          <>
            {/* ── Info do Leilão ────────────────────────────────────────────── */}
            <div className="bg-gradient-to-b from-gray-900 to-gray-800/50 border border-gray-700/50 rounded-2xl p-4 sm:p-6 text-center">
              <h1 className="text-lg sm:text-2xl font-black text-white leading-tight">
                {leilao.nome}
              </h1>

              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 text-xs sm:text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  {leilao.cidade}/{leilao.uf}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 flex-shrink-0" />
                  {fmtDate(leilao.data_leilao)}
                </span>
                {leilao.hora_inicio && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    {leilao.hora_inicio}
                  </span>
                )}
                {leilao.tipo_leilao && (
                  <span className="px-2 py-0.5 bg-indigo-500/15 text-indigo-300 text-xs rounded-full border border-indigo-500/30 font-medium">
                    {leilao.tipo_leilao.replace('_', ' ')}
                  </span>
                )}
                {lastUpdate && (
                  <span className="text-gray-600 tabular-nums">
                    · {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>

              {/* Barra de progresso */}
              <div className="mt-5 sm:mt-6">
                <ProgressBar
                  vendidos={stats.lotes_vendidos}
                  defendidos={stats.lotes_defendidos}
                  total={stats.total_lotes}
                />
              </div>
            </div>

            {/* ── Stats Cards (lotes + %) ───────────────────────────────────── */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <StatCard
                icon={Package}
                label="Vendidos"
                value={stats.lotes_vendidos.toLocaleString('pt-BR')}
                sub={`${stats.animais_vendidos.toLocaleString('pt-BR')} animais`}
                pct={stats.pct_vendidos}
                accent="emerald"
              />
              <StatCard
                icon={ShieldAlert}
                label="Defesas"
                value={stats.lotes_defendidos.toLocaleString('pt-BR')}
                sub={`${stats.animais_defendidos.toLocaleString('pt-BR')} animais`}
                pct={stats.pct_defendidos}
                accent="amber"
              />
              <StatCard
                icon={CircleDot}
                label="Não Movimentados"
                value={stats.lotes_indefinidos.toLocaleString('pt-BR')}
                sub={`de ${stats.total_lotes} lotes total`}
                pct={stats.pct_indefinidos}
                accent="violet"
              />
            </div>

            {/* ── Resumo financeiro (sempre visível) ────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <div className="bg-gray-800/40 border border-emerald-500/20 rounded-2xl p-3 sm:p-5">
                <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mb-1">Total Vendas</p>
                <p className="text-base sm:text-2xl font-black text-emerald-400 tabular-nums leading-tight">
                  {fmtCurrency(stats.total_vendas)}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">{stats.animais_vendidos} animais</p>
              </div>
              <div className="bg-gray-800/40 border border-amber-500/20 rounded-2xl p-3 sm:p-5">
                <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mb-1">Total Defesas</p>
                <p className="text-base sm:text-2xl font-black text-amber-400 tabular-nums leading-tight">
                  {fmtCurrency(stats.total_defesas)}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">{stats.animais_defendidos} animais</p>
              </div>
              <div className="bg-gray-800/40 border border-violet-500/20 rounded-2xl p-3 sm:p-5">
                <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mb-1">Comissões</p>
                <p className="text-base sm:text-2xl font-black text-violet-400 tabular-nums leading-tight">
                  {fmtCurrency(stats.total_comissoes + (stats.total_comissoes_defesa || 0))}
                </p>
                <div className="mt-1.5 space-y-0.5">
                  <p className="text-xs text-gray-500">
                    Compra: <span className="text-gray-300">{fmtCurrency(stats.total_comissoes_compra)}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    Venda: <span className="text-gray-300">{fmtCurrency(stats.total_comissoes_venda)}</span>
                  </p>
                  {stats.total_comissoes_defesa > 0 && (
                    <p className="text-xs text-gray-500">
                      Defesa: <span className="text-amber-400">{fmtCurrency(stats.total_comissoes_defesa)}</span>
                    </p>
                  )}
                  {(stats.total_gta > 0 || stats.total_nota > 0) && (
                    <p className="text-xs text-gray-500">
                      GTA + Nota: <span className="text-gray-300">{fmtCurrency((stats.total_gta || 0) + (stats.total_nota || 0))}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="bg-gray-800/40 border border-blue-500/20 rounded-2xl p-3 sm:p-5">
                <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mb-1">Total Animais</p>
                <p className="text-base sm:text-2xl font-black text-blue-400 tabular-nums leading-tight">
                  {stats.total_animais.toLocaleString('pt-BR')}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                  {stats.animais_vendidos + stats.animais_defendidos} movimentados
                </p>
              </div>
            </div>

            {/* ── Rankings ─────────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RankingCard
                title="Ranking de Compradores"
                icon={Users}
                items={rankings?.compradores}
                accent="blue"
              />
              <RankingCard
                title="Ranking de Vendedores"
                icon={Trophy}
                items={rankings?.vendedores}
                accent="emerald"
              />
            </div>

            {/* ── Link transmissão ─────────────────────────────────────────── */}
            {leilao.link_transmissao && (
              <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border border-indigo-500/30 rounded-2xl p-5 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                    <Radio className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Transmissão ao Vivo</p>
                    <p className="text-sm text-indigo-300">Este leilão está sendo transmitido</p>
                  </div>
                </div>
                <a
                  href={leilao.link_transmissao}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-bold transition-colors flex items-center gap-2"
                >
                  <Radio className="w-4 h-4" />
                  Assistir
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
